import React, { useState } from "react";
import { X, Copy, Check, KeyRound } from "lucide-react";
import { Bt } from "../components";

// Shown to whoever just created (or reset) a login, so the temporary password
// can be handed over even if the email never arrives. The same details are
// emailed to the employee; they are asked to set their own password on first
// sign-in.
//   items: [{ name, username, password, emailedTo }]
const loginLink = () => `${window.location.origin}/`;
const block = (it) => `HRMS login details${it.name ? ` for ${it.name}` : ""}\nUsername: ${it.username}\nTemporary password: ${it.password}\nSign in: ${loginLink()}\n(You will be asked to set a new password after signing in.)`;

const copyText = async (text) => {
  try { await navigator.clipboard.writeText(text); return true; }
  catch (_) { // clipboard API needs https / a user gesture; fall back to a hidden textarea
    try {
      const t = document.createElement("textarea"); t.value = text; t.style.position = "fixed"; t.style.opacity = "0";
      document.body.appendChild(t); t.select(); const ok = document.execCommand("copy"); document.body.removeChild(t); return ok;
    } catch (__) { return false; }
  }
};

const CopyBtn = ({ text, label }) => {
  const [done, setDone] = useState(false);
  return <button type="button" onClick={async () => { if (await copyText(text)) { setDone(true); setTimeout(() => setDone(false), 1500); } }}
    className="inline-flex items-center gap-1 px-[10px] py-[5px] rounded-lg border border-b2 bg-bg1 text-t1 text-[12px] font-semibold cursor-pointer">
    {done ? <Check size={12} /> : <Copy size={12} />}{done ? "Copied" : label}
  </button>;
};

export const CredentialsModal = ({ items, title = "New login created", onClose }) => {
  if (!items || !items.length) return null;
  return <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[300] flex items-center justify-center p-3" onMouseDown={onClose}>
    <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl p-4 sm:p-6 w-full max-w-[640px] max-h-[85vh] overflow-auto shadow-[0_20px_60px_rgba(0,0,0,0.15)]" onMouseDown={e => e.stopPropagation()}>
      <div className="flex justify-between items-center mb-3">
        <span className="text-base font-extrabold text-t0 flex items-center gap-2"><KeyRound size={16} />{title}</span>
        <button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer"><X size={18} /></button>
      </div>
      <div className="text-[12px] text-t3 mb-3">This temporary password is shown only now. The employee must set a new one the first time they sign in. Copy it and share it securely if the email does not reach them.</div>
      {items.map((it, i) => <div key={i} className="p-3 bg-bg2 border border-b0 rounded-[10px] mb-[10px]">
        {it.name && <div className="text-[14px] font-bold text-t0 mb-2">{it.name}</div>}
        <div className="grid grid-cols-[110px_1fr_auto] gap-y-2 gap-x-2 items-center text-[14px]">
          <span className="text-[12px] text-t3 uppercase">Email / Username</span><span className="font-mono text-t0 break-all">{it.username}</span><CopyBtn text={it.username} label="Copy" />
          <span className="text-[12px] text-t3 uppercase">Password</span><span className="font-mono font-bold text-t0 break-all">{it.password}</span><CopyBtn text={it.password} label="Copy" />
          <span className="text-[12px] text-t3 uppercase">Login link</span><span className="font-mono text-t1 break-all">{loginLink()}</span><CopyBtn text={loginLink()} label="Copy" />
        </div>
        <div className="flex items-center justify-between mt-3">
          <span className={`text-[12px] ${it.emailedTo ? "text-g" : "text-a"}`}>{it.emailedTo ? `Emailed to ${it.emailedTo}` : "Email was not sent — share these details manually"}</span>
          <CopyBtn text={block(it)} label="Copy all details" />
        </div>
      </div>)}
      <div className="flex justify-end gap-2 mt-2">
        {items.length > 1 && <CopyBtn text={items.map(block).join("\n\n")} label={`Copy all ${items.length}`} />}
        <Bt onClick={onClose}>Done</Bt>
      </div>
    </div>
  </div>;
};

// Normalises an API response ({ username, default_password, emailed_to }) into a modal item.
export const credsFrom = (res, name) => (res && res.default_password
  ? { name: name || res.name, username: res.username, password: res.default_password, emailedTo: res.emailed_to || null }
  : null);
